import React, { useState, useRef } from 'react';
import axios from 'axios';
import NTEECodes from '../NTEECodes';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AddGiftCard = ({ onGiftAdded, onCancel, currentPdfUrl, currentPdfSource }) => {
  const [newGift, setNewGift] = useState({
    recipient_name: '',
    donor_name: '',
    donor_type: '',
    gift_amount: '',
    gift_year: new Date().getFullYear(),
    gift_type: '',
    recipient_city: '',
    recipient_state: '',
    recipient_zip: '',
    recipient_ein: '',
    recipient_ntee_code: '',
    source_url: currentPdfUrl || '',
    source_pdf: currentPdfSource || ''
  });
  
  const [nteeSearchTerm, setNteeSearchTerm] = useState('');
  const [filteredNteeCodes, setFilteredNteeCodes] = useState([]);
  const [showNteeDropdown, setShowNteeDropdown] = useState(false);
  const [showProPublicaModal, setShowProPublicaModal] = useState(false);
  const [proPublicaSearchTerm, setProPublicaSearchTerm] = useState('');
  const [proPublicaSearchResults, setProPublicaSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  
  const nteeDropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Available gift types
  const giftTypes = ['Major Gift', 'Grant', 'Matching Gifts', 'Annual', 'Planned / Estate/ Bequest Gift', 'Board Member',
    'Capital / Campaign Gift', 'Consecutive', 'Other / Unknown', 'Cumulative', 'Donor Advised Funds',
    'Endowment Gifts', 'Event Sponsorship', 'In Kind', 'Memorial', 'Program / Restricted', 'Staff', 'Volunteers'];

  // Find NTEE description for a given code
  const getNteeDescription = (code) => {
    const nteeItem = NTEECodes.find(item => item.code === code);
    return nteeItem ? `${code} - ${nteeItem.description}` : code;
  };

  // Handle ProPublica search
  const handleProPublicaSearch = async () => {
    if (!proPublicaSearchTerm.trim()) return;
    
    setIsSearching(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(`${API_URL}/api/propublica/search`, {
        params: { query: proPublicaSearchTerm },
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data && response.data.organizations) {
        setProPublicaSearchResults(response.data.organizations);
      } else {
        setProPublicaSearchResults([]);
      }
    } catch (error) {
      console.error('Error searching ProPublica:', error);
      setProPublicaSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle organization selection from ProPublica results
  const handleOrganizationSelect = (organization) => {
    setNewGift(prev => ({
      ...prev,
      recipient_name: organization.name || prev.recipient_name,
      recipient_city: organization.city || prev.recipient_city,
      recipient_state: organization.state || prev.recipient_state,
      recipient_zip: organization.zipcode || prev.recipient_zip,
      recipient_ein: organization.ein || prev.recipient_ein,
      recipient_ntee_code: organization.ntee_code || prev.recipient_ntee_code
    }));
    
    setShowProPublicaModal(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'gift_amount') {
      setNewGift(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setNewGift(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_URL}/api/gifts/create`,
        { gift: newGift },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        onGiftAdded(response.data.gift);
      }
    } catch (error) {
      console.error('Error creating gift:', error);
      alert('Failed to create the gift. Please try again.');
    }
  };

  // ProPublica Modal Component
  const ProPublicaModal = () => {
    if (!showProPublicaModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-3xl">
          <h2 className="text-xl font-bold mb-4">ProPublica Nonprofit Lookup</h2>          
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              ref={inputRef}
              value={proPublicaSearchTerm}
              onChange={(e) => setProPublicaSearchTerm(e.target.value)}
              className="border p-2 rounded flex-1"
              placeholder="Enter organization name"
              onKeyDown={(e) => e.key === 'Enter' && handleProPublicaSearch()}
            />
            <button
              onClick={handleProPublicaSearch}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              disabled={isSearching}
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
          
          {proPublicaSearchResults.length > 0 ? (
            <div className="mt-4 max-h-96 overflow-y-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left">Name</th>
                    <th className="border p-2 text-left">Location</th>
                    <th className="border p-2 text-left">EIN</th>
                    <th className="border p-2 text-left">NTEE Code</th>
                    <th className="border p-2 text-left">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {proPublicaSearchResults.map((org) => (
                    <tr key={org.ein} className="hover:bg-gray-50">
                      <td className="border p-2">{org.name}</td>
                      <td className="border p-2">{`${org.city || ''}, ${org.state || ''} ${org.zipcode || ''}`}</td>
                      <td className="border p-2">{org.ein}</td>
                      <td className="border p-2">{org.ntee_code}</td>
                      <td className="border p-2">
                        <button
                          onClick={() => handleOrganizationSelect(org)}
                          className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                        >
                          Select
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            proPublicaSearchTerm && !isSearching && <p className="text-center py-4">No results found</p>
          )}
          
          <div className="flex justify-end mt-4">
            <button
              onClick={() => setShowProPublicaModal(false)}
              className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 mb-4 rounded-lg border bg-white border-gray-200">
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-lg font-semibold">
          <div className="flex gap-4 w-full">
            <input
              type="text"
              name="recipient_name"
              value={newGift.recipient_name}
              onChange={handleChange}
              className="border p-1 rounded w-full min-w-[320px]"
              placeholder="Recipient Name"
            />
            <em><small> or... </small></em>
            <button
              onClick={() => {
                setProPublicaSearchTerm(newGift.recipient_name);
                setShowProPublicaModal(true);
              }}
              className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm whitespace-nowrap"
            >
              ProPublica Lookup
            </button>
          </div>
        </h3>
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <p className="font-medium">Donor Information:</p>
          <input
            type="text"
            name="donor_name"
            value={newGift.donor_name}
            onChange={handleChange}
            className="border p-1 rounded w-full mb-2"
            placeholder="Donor Name"
          />
          <select
            name="donor_type"
            value={newGift.donor_type}
            onChange={handleChange}
            className="border p-1 rounded w-full mb-2"
          >
            <option value="" disabled>Select donor type</option>
            <option value="individual">Individual</option>
            <option value="business">Business</option>
            <option value="government">Government</option>
            <option value="unknown">Unknown</option>
          </select>
          
          <div className="mt-2">
            <p className="font-medium">Gift Details:</p>
            <div className="flex items-center mb-2">
              <span className="mr-1">$</span>
              <input
                type="number"
                name="gift_amount"
                value={newGift.gift_amount}
                onChange={handleChange}
                className="border p-1 rounded w-full"
                placeholder="Gift Amount"
              />
            </div>
            
            <input
              type="number"
              name="gift_year"
              value={newGift.gift_year}
              onChange={handleChange}
              className="border p-1 rounded w-full mb-2"
              min="1900"
              max="2100"
              placeholder="Gift Year"
            />
            
            <select
              name="gift_type"
              value={newGift.gift_type}
              onChange={handleChange}
              className="border p-1 rounded w-full"
            >
              <option value="" disabled>Select gift type</option>
              {giftTypes.map(type => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <p className="font-medium">Recipient Details:</p>
          <div className="grid grid-cols-6 gap-1 mb-2">
            <input
              type="text"
              name="recipient_city"
              value={newGift.recipient_city}
              onChange={handleChange}
              className="border p-1 rounded col-span-3"
              placeholder="City"
            />
            <input
              type="text"
              name="recipient_state"
              value={newGift.recipient_state}
              onChange={handleChange}
              className="border p-1 rounded col-span-1"
              placeholder="State"
              maxLength="2"
            />
            <input
              type="text"
              name="recipient_zip"
              value={newGift.recipient_zip}
              onChange={handleChange}
              className="border p-1 rounded col-span-2"
              placeholder="Zip"
            />
          </div>
          
          <div className="mt-2">
            <div className="mb-2">
              <span className="block text-xs text-gray-600">EIN:</span>
              <input
                type="text"
                name="recipient_ein"
                value={newGift.recipient_ein}
                onChange={handleChange}
                className="border p-1 rounded w-full"
                placeholder="EIN"
              />
            </div>
            <div className="relative" ref={nteeDropdownRef}>
              <span className="block text-xs text-gray-600">NTEE Code:</span>
              <input
                type="text"
                name="recipient_ntee_code"
                value={nteeSearchTerm || (newGift.recipient_ntee_code ? 
                  getNteeDescription(newGift.recipient_ntee_code)
                  : '')}
                onChange={(e) => {
                  setNteeSearchTerm(e.target.value);
                  setShowNteeDropdown(true);
                }}
                onClick={() => setShowNteeDropdown(true)}
                className="border p-1 rounded w-full"
                placeholder="Search NTEE Code or Description"
              />
              {showNteeDropdown && (
                <div className="absolute z-10 mt-1 w-full max-h-60 overflow-auto bg-white border border-gray-300 rounded shadow-lg">
                  {filteredNteeCodes.length > 0 ? (
                    filteredNteeCodes.map((ntee) => (
                      <div
                        key={ntee.code}
                        className="p-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => {
                          setNewGift(prev => ({
                            ...prev,
                            recipient_ntee_code: ntee.code
                          }));
                          setNteeSearchTerm(`${ntee.code} - ${ntee.description}`);
                          setShowNteeDropdown(false);
                        }}
                      >
                        {`${ntee.code} - ${ntee.description}`}
                      </div>
                    ))
                  ) : (
                    <div className="p-2 text-gray-500">No matching NTEE codes found</div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-4 flex justify-end space-x-2">
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Create Gift
        </button>
      </div>
      
      {/* Render the ProPublica lookup modal */}
      <ProPublicaModal />
    </div>
  );
};

export default AddGiftCard;

